/**
 * A call's recording link (card 21.7) as plain functions, tested without
 * rendering: the checks the API makes (crm-calls.ts recordingUrlOf) made as
 * the admin types, and what a browser's playback error means.
 *
 * The browser plays the link straight from where it is kept: the API never
 * fetches it. So a link that passes these checks can still fail to play (a
 * share page rather than the file, a link that needs signing in, a format the
 * browser cannot read); the player says which, and offers the link itself.
 *
 * Backend reference: ilbp-backend docs/CARD_21_7_CALL_RECORDINGS.md.
 */

/** The API's limit, and CallLog_recordingUrl_check's. */
export const RECORDING_URL_MAX = 2048;

export type RecordingCheck = { url: string | null; problem?: undefined } | { url?: undefined; problem: string };

/**
 * The link as the API will store it (the URL parser's form), or null for
 * none, or the reason it is refused, in the API's words.
 */
export const checkRecordingUrl = (text: string): RecordingCheck => {
  const value = text.trim();
  if (!value) return { url: null };
  const tooLong = { problem: `A recording link is at most ${RECORDING_URL_MAX.toLocaleString('en-US')} characters.` };
  if (value.length > RECORDING_URL_MAX) return tooLong;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return { problem: "Not a link. Paste the recording's whole address, starting https://." };
  }
  if (url.protocol !== 'https:') return { problem: 'Use an https:// link: browsers will not play a plain http recording in the admin panel.' };
  if (url.username || url.password) return { problem: 'Leave the sign-in out of the link: every admin who opens the call would see it.' };
  if (!url.hostname.includes('.') && !url.hostname.startsWith('[')) {
    return { problem: 'The link needs a full address, such as https://recordings.example.org/call.mp3.' };
  }
  if (url.href.length > RECORDING_URL_MAX) return tooLong;
  return { url: url.href };
};

/** "recordings.example.org": where a recording is kept, as a caption says it. */
export const recordingHost = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

/**
 * What a failed `<audio>` means (MediaError codes), in words an admin can act
 * on. Anything unknown reads as the commonest cause: not an audio file here.
 */
export const describeMediaError = (code: number | undefined) => {
  switch (code) {
    case 1:
      return 'Playback was stopped before the recording loaded.';
    case 2:
      return 'The recording could not be downloaded. Check your connection, or whether the link has expired.';
    case 3:
      return 'The file is damaged, or in a format this browser cannot play.';
    default:
      return 'This link does not play here. It may be a web page rather than the audio file, need signing in, or be in a format this browser cannot play.';
  }
};

/** The speeds the player steps through: calls are often reviewed fast. */
export const PLAYBACK_RATES = [1, 1.25, 1.5, 2] as const;

export const nextRate = (rate: number) => PLAYBACK_RATES[(PLAYBACK_RATES.indexOf(rate as (typeof PLAYBACK_RATES)[number]) + 1) % PLAYBACK_RATES.length];
